import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { RootState } from '~/redux/store'

import { startListening } from '../listenerMiddleware'
import { updateUserMeta } from '../apis/user-meta-api'

export interface PetState {
  eggState: number
  dragonPresent: boolean
  eggChosen: 'red' | 'green' | 'blue' | null
  tempEggChosen?: 'red' | 'green' | 'blue' | null
  name: string
  level: number
  dragonRank: number
  lifeLength: number
  stillHatching: boolean
  kudosOrbs: number
  totalMathEnergy: number
  totalAffection: number
  currentMathEnergyLevel: number
  currentAffectionLevel: number
  lastTimeStamp: number
  hatchingCooldown: number
  hatchingEnergyCooldown: number[]
  tricksLearned: string[]
  currentTrickLearning: null
  currentTrickProgress: number
  trickLearningCooldown: number
}

interface State {
  petState: PetState | undefined
}

const initialState: State = {
  petState: {
    eggState: 0,
    dragonPresent: false,
    eggChosen: null,
    tempEggChosen: null,
    name: 'Mathagon',
    level: 0,
    dragonRank: 0,
    lifeLength: 0,
    stillHatching: true,
    kudosOrbs: 0,
    totalMathEnergy: 0,
    totalAffection: 0,
    currentMathEnergyLevel: 0,
    currentAffectionLevel: 50,
    lastTimeStamp: 0,
    hatchingCooldown: 0,
    hatchingEnergyCooldown: [0, 0, 0, 0, 0],
    tricksLearned: [],
    currentTrickLearning: null,
    currentTrickProgress: 0,
    trickLearningCooldown: 0,
  },
}

const slice = createSlice({
  name: 'pet-state',
  initialState,
  reducers: {
    setPetState: (state, action: PayloadAction<PetState>) => ({
      ...state,
      petState: action.payload,
    }),
    resetPetState: () => ({
      ...initialState,
    }),
  },
})

export const selectPetState = (state: RootState): PetState | undefined => state.petState.petState
export const { setPetState: setPetStateActionCreator, resetPetState: resetPetStateActionCreator } = slice.actions

startListening({
  actionCreator: setPetStateActionCreator,
  effect: (state, listenerApi) => {
    const { name, stillHatching, lifeLength } = state.payload

    const isNameUpdated =
      name !== listenerApi.getOriginalState()?.petState?.petState?.name && name !== initialState.petState?.name
    const dragonHatched =
      lifeLength !== listenerApi.getOriginalState()?.petState?.petState?.lifeLength &&
      lifeLength !== initialState.petState?.lifeLength &&
      !stillHatching

    if (isNameUpdated || dragonHatched) {
      listenerApi.dispatch(
        updateUserMeta.initiate({
          petName: isNameUpdated || dragonHatched ? state.payload.name : undefined,
          dragonHatchDate: dragonHatched ? lifeLength : undefined,
        }),
      )
    }
  },
})

export default slice.reducer
